import React, { useState, useEffect } from 'react';
import { apiRequest } from '@/utils/api-request';
import { Users, X, Bot, Bell, Plus, Edit2, Trash2, Loader2, Mail, Phone, MessageCircle, DollarSign, Facebook, Calendar, Webhook, Activity, BarChart3, RotateCcw, Check, AlertCircle, Search, Loader, Star } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';

interface ClientSchool {
    slate_id: number;
    slate_name: string;
    automation: string;
    automation_distance: number | null;
    automation_grades: string | null;
    is_spotlight: number;
    city: number;
    area_id_fk: number;
    city_name: string;
    area_name: string;
    notification_contacts_count: number;
    webhook_count: number;
}

const GRADE_OPTIONS = [
    { value: 'playgroup', label: 'Playgroup' },
    { value: 'pre_nursery', label: 'Pre-Nursery' },
    { value: 'nursery', label: 'Nursery' },
    { value: 'pre_k', label: 'Pre-K' },
    { value: 'lkg', label: 'LKG' },
    { value: 'ukg', label: 'UKG' },
    { value: 'grade_1', label: 'Grade 1' },
    { value: 'grade_2', label: 'Grade 2' },
    { value: 'grade_3', label: 'Grade 3' },
    { value: 'grade_4', label: 'Grade 4' },
    { value: 'grade_5', label: 'Grade 5' },
    { value: 'grade_6', label: 'Grade 6' },
    { value: 'grade_7', label: 'Grade 7' },
    { value: 'grade_8', label: 'Grade 8' },
    { value: 'grade_9', label: 'Grade 9' },
    { value: 'grade_10', label: 'Grade 10' },
    { value: 'grade_11', label: 'Grade 11' },
    { value: 'grade_12', label: 'Grade 12' },
];

interface NotificationContact {
    id: number;
    slate_id: number;
    name: string;
    designation: string | null;
    phone: string | null;
    email: string | null;
    notify_email: boolean;
    notify_whatsapp: boolean;
    is_active: boolean;
}

interface ContactFormData {
    name: string;
    designation: string;
    phone: string;
    email: string;
    notify_email: boolean;
    notify_whatsapp: boolean;
    is_active: boolean;
}

interface FbBudget {
    id: number;
    slate_id: number;
    month: number;
    year: number;
    budget: number;
}

interface BudgetFormData {
    month: number;
    year: number;
    budget: string;
}

interface Webhook {
    id: number;
    client_id: number;
    webhook_name: string;
    webhook_url: string;
    is_active: boolean;
    created_at: string;
}

interface WebhookLog {
    id: number;
    webhook_id: number;
    lead_id?: number;
    status: 'sent' | 'failed';
    status_code?: number;
    request_payload?: any;
    response_data?: any;
    error_message?: string;
    sent_at: string;
}

interface WebhookStats {
    total: number;
    success: number;
    duplicate: number;
    apiError: number;
    networkError: number;
    successRate: number;
}

const initialContactForm: ContactFormData = {
    name: '',
    designation: '',
    phone: '',
    email: '',
    notify_email: true,
    notify_whatsapp: false,
    is_active: true,
};

const currentDate = new Date();
const initialBudgetForm: BudgetFormData = {
    month: currentDate.getMonth() + 1,
    year: currentDate.getFullYear(),
    budget: '',
};

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const ClientsAdmin: React.FC = () => {
    const [schools, setSchools] = useState<ClientSchool[]>([]);
    const [filteredSchools, setFilteredSchools] = useState<ClientSchool[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSchool, setSelectedSchool] = useState<ClientSchool | null>(null);
    const [activeTab, setActiveTab] = useState<'automation' | 'notifications' | 'budget' | 'webhooks'>('automation');

    // Automation state
    const [updatingAutomation, setUpdatingAutomation] = useState(false);

    // Spotlight state
    const [updatingSpotlight, setUpdatingSpotlight] = useState(false);
    const [bulkingSpotlight, setBulkingSpotlight] = useState(false);
    const [showAutomationModal, setShowAutomationModal] = useState(false);
    const [automationDistance, setAutomationDistance] = useState<number>(7);
    const [automationGrades, setAutomationGrades] = useState<string[]>([]);

    // Notification contacts state
    const [contacts, setContacts] = useState<NotificationContact[]>([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [editingContact, setEditingContact] = useState<NotificationContact | null>(null);
    const [contactForm, setContactForm] = useState<ContactFormData>(initialContactForm);
    const [savingContact, setSavingContact] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    // FB Budget state
    const [budgets, setBudgets] = useState<FbBudget[]>([]);
    const [budgetsLoading, setBudgetsLoading] = useState(false);
    const [budgetForm, setBudgetForm] = useState<BudgetFormData>(initialBudgetForm);
    const [savingBudget, setSavingBudget] = useState(false);
    const [deleteBudgetConfirm, setDeleteBudgetConfirm] = useState<number | null>(null);

    // Webhooks state
    const [webhooks, setWebhooks] = useState<Webhook[]>([]);
    const [webhooksLoading, setWebhooksLoading] = useState(false);
    const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [logs, setLogs] = useState<WebhookLog[]>([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsHasMore, setLogsHasMore] = useState(false);
    const [logsStartDate, setLogsStartDate] = useState('');
    const [logsEndDate, setLogsEndDate] = useState('');
    const [logsSearch, setLogsSearch] = useState('');
    const [logsStatusFilter, setLogsStatusFilter] = useState('');
    const [stats, setStats] = useState<WebhookStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [resendingLogId, setResendingLogId] = useState<number | null>(null);

    // Fetch client schools
    const fetchClientSchools = async () => {
        try {
            setIsLoading(true);
            const response = await apiRequest('/api/schools/clients/automation', { method: 'GET' });
            if (response.success) {
                const schoolsData = response.data.schools || [];
                setSchools(schoolsData);
                setFilteredSchools(schoolsData);
            } else {
                showToast('Failed to fetch client schools', 'error');
            }
        } catch (error) {
            console.error('Error fetching client schools:', error);
            showToast('Failed to fetch client schools', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Filter schools by search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredSchools(schools);
        } else {
            const term = searchTerm.toLowerCase();
            setFilteredSchools(schools.filter(school =>
                school.slate_name.toLowerCase().includes(term) ||
                school.city_name?.toLowerCase().includes(term) ||
                school.area_name?.toLowerCase().includes(term)
            ));
        }
    }, [searchTerm, schools]);

    // Fetch notification contacts for selected school
    const fetchContacts = async (slateId: number) => {
        try {
            setContactsLoading(true);
            const response = await apiRequest(`/schools/${slateId}/notification-contacts`);
            if (response.success) {
                setContacts(response.data?.contacts || []);
            }
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setContactsLoading(false);
        }
    };

    // Fetch FB budgets for selected school
    const fetchBudgets = async (slateId: number) => {
        try {
            setBudgetsLoading(true);
            const response = await apiRequest(`/schools/${slateId}/fb-budgets`);
            if (response.success) {
                setBudgets(response.data?.budgets || []);
            }
        } catch (error) {
            console.error('Error fetching budgets:', error);
        } finally {
            setBudgetsLoading(false);
        }
    };

    // Open automation settings modal when enabling
    const handleToggleAutomation = () => {
        if (!selectedSchool) return;
        if (selectedSchool.automation === 'start') {
            // Disabling — no modal needed
            applyAutomation('stop', 7, []);
        } else {
            // Enabling — open settings modal with current or default values
            setAutomationDistance(selectedSchool.automation_distance ?? 7);
            setAutomationGrades(selectedSchool.automation_grades ? JSON.parse(selectedSchool.automation_grades) : []);
            setShowAutomationModal(true);
        }
    };

    const openEditAutomationSettings = () => {
        if (!selectedSchool) return;
        setAutomationDistance(selectedSchool.automation_distance ?? 7);
        setAutomationGrades(selectedSchool.automation_grades ? JSON.parse(selectedSchool.automation_grades) : []);
        setShowAutomationModal(true);
    };

    const applyAutomation = async (action: 'start' | 'stop', distance: number, grades: string[]) => {
        if (!selectedSchool) return;
        try {
            setUpdatingAutomation(true);
            setShowAutomationModal(false);
            const response = await apiRequest(`/api/schools/clients/automation/${selectedSchool.slate_id}`, {
                method: 'PUT',
                body: JSON.stringify({ automation: action, distance, grades }),
            });
            if (response.success) {
                const updated = {
                    ...selectedSchool,
                    automation: action,
                    automation_distance: action === 'start' ? distance : selectedSchool.automation_distance,
                    automation_grades: action === 'start' ? JSON.stringify(grades) : selectedSchool.automation_grades,
                };
                setSelectedSchool(updated);
                setSchools(prev => prev.map(s => s.slate_id === selectedSchool.slate_id ? { ...s, ...updated } : s));
                showToast(response.data?.message || `Automation ${action === 'start' ? 'enabled' : 'disabled'}`, 'success');
            } else {
                showToast(response.message || 'Failed to update automation', 'error');
            }
        } catch {
            showToast('Failed to update automation', 'error');
        } finally {
            setUpdatingAutomation(false);
        }
    };

    // Toggle spotlight for selected school
    const handleToggleSpotlight = async () => {
        if (!selectedSchool) return;
        const newValue = !!selectedSchool.is_spotlight ? 0 : 1;
        try {
            setUpdatingSpotlight(true);
            const response = await apiRequest(`/api/schools/clients/spotlight/${selectedSchool.slate_id}`, {
                method: 'PUT',
                body: JSON.stringify({ is_spotlight: newValue }),
            });
            if (response.success) {
                const updated = { ...selectedSchool, is_spotlight: newValue };
                setSelectedSchool(updated);
                setSchools(prev => prev.map(s => s.slate_id === selectedSchool.slate_id ? { ...s, is_spotlight: newValue } : s));
                showToast(`Spotlight ${newValue === 1 ? 'enabled' : 'disabled'} for ${selectedSchool.slate_name}`, 'success');
            } else {
                showToast(response.message || 'Failed to update spotlight', 'error');
            }
        } catch {
            showToast('Failed to update spotlight', 'error');
        } finally {
            setUpdatingSpotlight(false);
        }
    };

    // Bulk enable/disable spotlight for all client schools
    const handleBulkSpotlight = async (enable: boolean) => {
        try {
            setBulkingSpotlight(true);
            const response = await apiRequest('/api/schools/clients/spotlight/bulk', {
                method: 'PUT',
                body: JSON.stringify({ is_spotlight: enable ? 1 : 0 }),
            });
            if (response.success) {
                setSchools(prev => prev.map(s => ({ ...s, is_spotlight: enable ? 1 : 0 })));
                if (selectedSchool) setSelectedSchool(prev => prev ? { ...prev, is_spotlight: enable ? 1 : 0 } : prev);
                showToast(`Spotlight ${enable ? 'enabled' : 'disabled'} for all client schools`, 'success');
            } else {
                showToast(response.message || 'Failed to bulk update spotlight', 'error');
            }
        } catch {
            showToast('Failed to bulk update spotlight', 'error');
        } finally {
            setBulkingSpotlight(false);
        }
    };

    // Open panel for a school
    const openManagePanel = (school: ClientSchool) => {
        setSelectedSchool(school);
        setActiveTab('automation');
        fetchContacts(school.slate_id);
        fetchBudgets(school.slate_id);
        fetchWebhooks(school.slate_id);
    };

    // Close panel
    const closePanel = () => {
        setSelectedSchool(null);
        setContacts([]);
        setBudgets([]);
        setWebhooks([]);
        setShowContactModal(false);
        setEditingContact(null);
        setContactForm(initialContactForm);
        setBudgetForm(initialBudgetForm);
        setShowLogsModal(false);
        setShowStatsModal(false);
        setSelectedWebhook(null);
    };

    // Contact CRUD
    const openContactModal = (contact?: NotificationContact) => {
        if (contact) {
            setEditingContact(contact);
            setContactForm({
                name: contact.name,
                designation: contact.designation || '',
                phone: contact.phone || '',
                email: contact.email || '',
                notify_email: contact.notify_email,
                notify_whatsapp: contact.notify_whatsapp,
                is_active: contact.is_active,
            });
        } else {
            setEditingContact(null);
            setContactForm(initialContactForm);
        }
        setShowContactModal(true);
    };

    const closeContactModal = () => {
        setShowContactModal(false);
        setEditingContact(null);
        setContactForm(initialContactForm);
    };

    const saveContact = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSchool) return;
        if (!contactForm.name.trim()) {
            showToast('Name is required', 'error');
            return;
        }
        if (!contactForm.phone && !contactForm.email) {
            showToast('At least phone or email is required', 'error');
            return;
        }
        try {
            setSavingContact(true);
            const url = editingContact
                ? `/schools/${selectedSchool.slate_id}/notification-contacts/${editingContact.id}`
                : `/schools/${selectedSchool.slate_id}/notification-contacts`;
            const method = editingContact ? 'PUT' : 'POST';
            const response = await apiRequest(url, { method, body: contactForm });
            if (response.success) {
                showToast(editingContact ? 'Contact updated' : 'Contact created', 'success');
                closeContactModal();
                fetchContacts(selectedSchool.slate_id);
                // Update count in schools list if new contact was created and is active
                if (!editingContact && contactForm.is_active) {
                    setSchools(prev => prev.map(s =>
                        s.slate_id === selectedSchool.slate_id
                            ? { ...s, notification_contacts_count: (s.notification_contacts_count || 0) + 1 }
                            : s
                    ));
                }
            } else {
                showToast(response.message || 'Failed to save contact', 'error');
            }
        } catch (error) {
            console.error('Error saving contact:', error);
            showToast('Failed to save contact', 'error');
        } finally {
            setSavingContact(false);
        }
    };

    const deleteContact = async (contactId: number) => {
        if (!selectedSchool) return;
        // Find if the contact being deleted was active
        const contactToDelete = contacts.find(c => c.id === contactId);
        try {
            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/notification-contacts/${contactId}`, { method: 'DELETE' });
            if (response.success) {
                showToast('Contact deleted', 'success');
                setDeleteConfirm(null);
                fetchContacts(selectedSchool.slate_id);
                // Update count in schools list if deleted contact was active
                if (contactToDelete?.is_active) {
                    setSchools(prev => prev.map(s =>
                        s.slate_id === selectedSchool.slate_id
                            ? { ...s, notification_contacts_count: Math.max(0, (s.notification_contacts_count || 0) - 1) }
                            : s
                    ));
                }
            } else {
                showToast(response.message || 'Failed to delete contact', 'error');
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
            showToast('Failed to delete contact', 'error');
        }
    };

    const toggleContactActive = async (contact: NotificationContact) => {
        if (!selectedSchool) return;
        try {
            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/notification-contacts/${contact.id}`, {
                method: 'PUT',
                body: { is_active: !contact.is_active },
            });
            if (response.success) {
                fetchContacts(selectedSchool.slate_id);
                // Update count in schools list
                const delta = contact.is_active ? -1 : 1; // Deactivating = -1, Activating = +1
                setSchools(prev => prev.map(s =>
                    s.slate_id === selectedSchool.slate_id
                        ? { ...s, notification_contacts_count: Math.max(0, (s.notification_contacts_count || 0) + delta) }
                        : s
                ));
            }
        } catch (error) {
            console.error('Error toggling contact status:', error);
        }
    };

    // Budget CRUD
    const saveBudget = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSchool) return;
        const budgetValue = parseFloat(budgetForm.budget);
        if (isNaN(budgetValue) || budgetValue < 0) {
            showToast('Please enter a valid budget amount', 'error');
            return;
        }
        try {
            setSavingBudget(true);
            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/fb-budgets`, {
                method: 'POST',
                body: {
                    month: budgetForm.month,
                    year: budgetForm.year,
                    budget: budgetValue,
                },
            });
            if (response.success) {
                showToast('Budget saved', 'success');
                setBudgetForm(initialBudgetForm);
                fetchBudgets(selectedSchool.slate_id);
            } else {
                showToast(response.message || 'Failed to save budget', 'error');
            }
        } catch (error) {
            console.error('Error saving budget:', error);
            showToast('Failed to save budget', 'error');
        } finally {
            setSavingBudget(false);
        }
    };

    const deleteBudget = async (budgetId: number) => {
        if (!selectedSchool) return;
        try {
            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/fb-budgets/${budgetId}`, { method: 'DELETE' });
            if (response.success) {
                showToast('Budget deleted', 'success');
                setDeleteBudgetConfirm(null);
                fetchBudgets(selectedSchool.slate_id);
            } else {
                showToast(response.message || 'Failed to delete budget', 'error');
            }
        } catch (error) {
            console.error('Error deleting budget:', error);
            showToast('Failed to delete budget', 'error');
        }
    };

    const editBudget = (budget: FbBudget) => {
        setBudgetForm({
            month: budget.month,
            year: budget.year,
            budget: budget.budget.toString(),
        });
    };

    // Fetch webhooks for selected school
    const fetchWebhooks = async (slateId: number) => {
        try {
            setWebhooksLoading(true);
            const response = await apiRequest(`/schools/${slateId}/webhooks`);
            if (response.success) {
                setWebhooks(response.data?.webhooks || []);
            }
        } catch (error) {
            console.error('Error fetching webhooks:', error);
        } finally {
            setWebhooksLoading(false);
        }
    };

    // View webhook logs
    const handleViewLogs = async (webhook: Webhook) => {
        if (!selectedSchool) return;
        setSelectedWebhook(webhook);
        setLogs([]);
        setLogsStartDate('');
        setLogsEndDate('');
        setLogsSearch('');
        setLogsStatusFilter('');
        setShowLogsModal(true);
        setLogsLoading(true);

        try {
            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/webhooks/${webhook.id}/logs?limit=50&offset=0`) as any;
            if (response.success) {
                setLogs(response.data || []);
                setLogsTotal(response.pagination?.total || 0);
                setLogsHasMore(response.pagination?.hasMore || false);
            }
        } catch (error) {
            showToast('Failed to fetch logs', 'error');
        } finally {
            setLogsLoading(false);
        }
    };

    // Filter logs
    const handleFilterLogs = async (statusFilterOverride?: string) => {
        if (!selectedSchool || !selectedWebhook) return;
        setLogs([]);
        setLogsLoading(true);
        const statusFilter = statusFilterOverride !== undefined ? statusFilterOverride : logsStatusFilter;

        try {
            const params = new URLSearchParams({ limit: '50', offset: '0' });
            if (logsStartDate) params.append('start_date', logsStartDate);
            if (logsEndDate) params.append('end_date', logsEndDate);
            if (logsSearch) params.append('search', logsSearch);
            if (statusFilter) params.append('status_filter', statusFilter);

            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/webhooks/${selectedWebhook.id}/logs?${params}`) as any;
            if (response.success) {
                setLogs(response.data || []);
                setLogsTotal(response.pagination?.total || 0);
                setLogsHasMore(response.pagination?.hasMore || false);
            }
        } catch (error) {
            showToast('Failed to fetch logs', 'error');
        } finally {
            setLogsLoading(false);
        }
    };

    // Load more logs
    const handleLoadMoreLogs = async () => {
        if (!selectedSchool || !selectedWebhook || logsLoading) return;
        setLogsLoading(true);

        try {
            const params = new URLSearchParams({ limit: '50', offset: logs.length.toString() });
            if (logsStartDate) params.append('start_date', logsStartDate);
            if (logsEndDate) params.append('end_date', logsEndDate);
            if (logsSearch) params.append('search', logsSearch);
            if (logsStatusFilter) params.append('status_filter', logsStatusFilter);

            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/webhooks/${selectedWebhook.id}/logs?${params}`) as any;
            if (response.success) {
                setLogs(prev => [...prev, ...(response.data || [])]);
                setLogsTotal(response.pagination?.total || 0);
                setLogsHasMore(response.pagination?.hasMore || false);
            }
        } catch (error) {
            showToast('Failed to load more logs', 'error');
        } finally {
            setLogsLoading(false);
        }
    };

    // View webhook stats
    const handleViewStats = async (webhook: Webhook) => {
        if (!selectedSchool) return;
        setSelectedWebhook(webhook);
        setStats(null);
        setShowStatsModal(true);
        setStatsLoading(true);

        try {
            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/webhooks/${webhook.id}/stats`);
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            showToast('Failed to fetch stats', 'error');
        } finally {
            setStatsLoading(false);
        }
    };

    // Resend webhook
    const handleResendWebhook = async (logId: number) => {
        if (!selectedSchool || !selectedWebhook) return;
        setResendingLogId(logId);

        try {
            const response = await apiRequest(`/schools/${selectedSchool.slate_id}/webhooks/${selectedWebhook.id}/resend/${logId}`, { method: 'POST' });
            if (response.success) {
                showToast('Webhook resent successfully', 'success');
                handleFilterLogs();
            } else {
                showToast(response.message || 'Failed to resend webhook', 'error');
            }
        } catch (error) {
            showToast('Failed to resend webhook', 'error');
        } finally {
            setResendingLogId(null);
        }
    };

    useEffect(() => {
        fetchClientSchools();
    }, []);

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <Users className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Clients</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {filteredSchools.length}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">Manage client schools - automation, notifications, etc.</p>
                        </div>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search schools..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-4 py-2 w-64 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 mt-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider max-w-xs">School Name</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">City</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Area</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24">Automation</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24">Spotlight</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-28">Contacts</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-28">Webhooks</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                            ) : filteredSchools.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">{searchTerm ? 'No schools match your search' : 'No client schools found'}</td></tr>
                            ) : (
                                filteredSchools.map((school, idx) => (
                                    <tr key={school.slate_id} className={`hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                        <td className="px-4 py-2 max-w-xs">
                                            <button
                                                onClick={() => openManagePanel(school)}
                                                className="text-sm font-semibold text-brandColor hover:text-brandColor/80 hover:underline text-left truncate block max-w-full"
                                                title={school.slate_name}
                                            >
                                                {school.slate_name}
                                            </button>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">{school.city_name || '-'}</td>
                                        <td className="px-4 py-2 text-sm text-gray-600">{school.area_name || '-'}</td>
                                        <td className="px-4 py-2 text-center">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${school.automation === 'start' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                                {school.automation === 'start' ? 'ON' : 'OFF'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${!!school.is_spotlight ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}>
                                                <Star className="h-3 w-3" />
                                                {!!school.is_spotlight ? 'ON' : 'OFF'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${school.notification_contacts_count > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
                                                <Bell className="h-3 w-3" />
                                                {school.notification_contacts_count || 0}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${school.webhook_count > 0 ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-500'}`}>
                                                <Webhook className="h-3 w-3" />
                                                {school.webhook_count || 0}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer Stats */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">
                        {searchTerm ? `Showing ${filteredSchools.length} of ${schools.length} clients` : `Total: ${schools.length} clients`}
                    </span>
                    <span className="text-gray-300">|</span>
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 text-yellow-500" />
                        Spotlight: {schools.filter(s => !!s.is_spotlight).length} ON
                    </span>
                    <button
                        onClick={() => handleBulkSpotlight(true)}
                        disabled={bulkingSpotlight}
                        className="text-xs px-3 py-1 rounded border border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 disabled:opacity-50"
                    >
                        Enable All
                    </button>
                    <button
                        onClick={() => handleBulkSpotlight(false)}
                        disabled={bulkingSpotlight}
                        className="text-xs px-3 py-1 rounded border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 disabled:opacity-50"
                    >
                        Disable All
                    </button>
                </div>
                <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        Automation ON: {filteredSchools.filter(s => s.automation === 'start').length}
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-gray-300"></span>
                        Automation OFF: {filteredSchools.filter(s => s.automation !== 'start').length}
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                        With Webhooks: {filteredSchools.filter(s => s.webhook_count > 0).length}
                    </span>
                </div>
            </div>

            {/* Slide-over Panel */}
            {selectedSchool && (
                <div className="fixed inset-0 z-50 overflow-hidden">
                    <div className="absolute inset-0 bg-black/30" onClick={closePanel}></div>
                    <div className="absolute inset-y-0 right-0 flex max-w-full">
                        <div className="w-screen max-w-2xl bg-white shadow-xl flex flex-col">
                            {/* Panel Header */}
                            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">{selectedSchool.slate_name}</h2>
                                    <p className="text-sm text-gray-500">{selectedSchool.city_name}, {selectedSchool.area_name}</p>
                                </div>
                                <button onClick={closePanel} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                                    <X className="h-5 w-5 text-gray-500" />
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="border-b border-gray-200">
                                <div className="flex">
                                    <button
                                        onClick={() => setActiveTab('automation')}
                                        className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'automation' ? 'border-brandColor text-brandColor' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                    >
                                        <Bot className="h-4 w-4" />
                                        Automation
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('notifications')}
                                        className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'notifications' ? 'border-brandColor text-brandColor' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                    >
                                        <Bell className="h-4 w-4" />
                                        Notifications
                                        {contacts.length > 0 && (
                                            <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">{contacts.length}</span>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('budget')}
                                        className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'budget' ? 'border-brandColor text-brandColor' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                    >
                                        <Facebook className="h-4 w-4" />
                                        FB Budget
                                        {budgets.length > 0 && (
                                            <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">{budgets.length}</span>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('webhooks')}
                                        className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === 'webhooks' ? 'border-brandColor text-brandColor' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                    >
                                        <Webhook className="h-4 w-4" />
                                        Webhooks
                                        {webhooks.length > 0 && (
                                            <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">{webhooks.length}</span>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Tab Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {activeTab === 'automation' && (
                                    <div className="space-y-6">
                                        <div className="bg-gray-50 rounded-lg p-6">
                                            <h3 className="text-sm font-medium text-gray-700 mb-4">Lead Automation</h3>
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Automatically process and assign leads</p>
                                                    <p className={`text-xs mt-1 ${selectedSchool.automation === 'start' ? 'text-green-600' : 'text-gray-500'}`}>
                                                        Status: {selectedSchool.automation === 'start' ? 'Enabled' : 'Disabled'}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={handleToggleAutomation}
                                                    disabled={updatingAutomation}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${selectedSchool.automation === 'start' ? 'bg-green-500' : 'bg-gray-300'} ${updatingAutomation ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${selectedSchool.automation === 'start' ? 'translate-x-6' : 'translate-x-1'}`} />
                                                </button>
                                            </div>
                                        </div>

                                        {selectedSchool.automation === 'start' && (
                                            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="text-sm font-medium text-gray-700">Automation Settings</h4>
                                                    <button onClick={openEditAutomationSettings} className="text-sm font-medium text-brandColor hover:underline">Edit</button>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                                    <span className="font-medium">Distance:</span>
                                                    <span>{selectedSchool.automation_distance === 0 ? 'City-based' : `${selectedSchool.automation_distance ?? 7} km`}</span>
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    <span className="font-medium">Grades:</span>{' '}
                                                    {selectedSchool.automation_grades && JSON.parse(selectedSchool.automation_grades).length > 0
                                                        ? JSON.parse(selectedSchool.automation_grades)
                                                            .map((g: string) => GRADE_OPTIONS.find(o => o.value === g)?.label || g)
                                                            .join(', ')
                                                        : <span className="text-gray-400">All grades</span>
                                                    }
                                                </div>
                                            </div>
                                        )}

                                        {/* Spotlight */}
                                        <div className="bg-gray-50 rounded-lg p-6">
                                            <h3 className="text-sm font-medium text-gray-700 mb-4">Spotlight</h3>
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Show this school as a spotlight school on the website</p>
                                                    <p className={`text-xs mt-1 ${!!selectedSchool.is_spotlight ? 'text-yellow-600' : 'text-gray-500'}`}>
                                                        Status: {!!selectedSchool.is_spotlight ? 'Enabled' : 'Disabled'}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={handleToggleSpotlight}
                                                    disabled={updatingSpotlight}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${!!selectedSchool.is_spotlight ? 'bg-yellow-400' : 'bg-gray-300'} ${updatingSpotlight ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!!selectedSchool.is_spotlight ? 'translate-x-6' : 'translate-x-1'}`} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'notifications' && (
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-sm font-medium text-gray-700">Notification Contacts</h3>
                                            <button
                                                onClick={() => openContactModal()}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover"
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                                Add Contact
                                            </button>
                                        </div>

                                        {contactsLoading ? (
                                            <div className="text-center py-8 text-gray-500">Loading contacts...</div>
                                        ) : contacts.length === 0 ? (
                                            <div className="text-center py-8">
                                                <Bell className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                                                <p className="text-sm text-gray-500">No notification contacts yet</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {contacts.map((contact) => (
                                                    <div key={contact.id} className={`p-4 border rounded-lg ${contact.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-gray-900">{contact.name}</span>
                                                                    {!contact.is_active && (
                                                                        <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">Inactive</span>
                                                                    )}
                                                                </div>
                                                                {contact.designation && (
                                                                    <p className="text-xs text-gray-500">{contact.designation}</p>
                                                                )}
                                                                <div className="mt-2 space-y-1">
                                                                    {contact.phone && (
                                                                        <div className="flex items-center text-xs text-gray-600">
                                                                            <Phone className="h-3 w-3 mr-1.5 text-gray-400" />
                                                                            {contact.phone}
                                                                        </div>
                                                                    )}
                                                                    {contact.email && (
                                                                        <div className="flex items-center text-xs text-gray-600">
                                                                            <Mail className="h-3 w-3 mr-1.5 text-gray-400" />
                                                                            {contact.email}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex gap-2 mt-2">
                                                                    {contact.notify_email && (
                                                                        <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                                                            <Mail className="h-3 w-3 mr-1" />Email
                                                                        </span>
                                                                    )}
                                                                    {contact.notify_whatsapp && (
                                                                        <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                                                            <MessageCircle className="h-3 w-3 mr-1" />WhatsApp
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <button onClick={() => toggleContactActive(contact)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title={contact.is_active ? 'Deactivate' : 'Activate'}>
                                                                    {contact.is_active ? '🟢' : '⚪'}
                                                                </button>
                                                                <button onClick={() => openContactModal(contact)} className="p-1.5 text-gray-400 hover:text-brandColor hover:bg-gray-100 rounded">
                                                                    <Edit2 className="h-4 w-4" />
                                                                </button>
                                                                {deleteConfirm === contact.id ? (
                                                                    <div className="flex items-center gap-1">
                                                                        <button onClick={() => deleteContact(contact.id)} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Yes</button>
                                                                        <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded">No</button>
                                                                    </div>
                                                                ) : (
                                                                    <button onClick={() => setDeleteConfirm(contact.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded">
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'budget' && (
                                    <div className="space-y-6">
                                        {/* Add/Edit Budget Form */}
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <h3 className="text-sm font-medium text-gray-700 mb-3">Set Monthly FB Budget</h3>
                                            <form onSubmit={saveBudget} className="space-y-3">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                                                        <select
                                                            value={budgetForm.month}
                                                            onChange={(e) => setBudgetForm({ ...budgetForm, month: parseInt(e.target.value) })}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                                                        >
                                                            {MONTHS.map((month, idx) => (
                                                                <option key={idx} value={idx + 1}>{month}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                                                        <select
                                                            value={budgetForm.year}
                                                            onChange={(e) => setBudgetForm({ ...budgetForm, year: parseInt(e.target.value) })}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                                                        >
                                                            {[2024, 2025, 2026, 2027].map((year) => (
                                                                <option key={year} value={year}>{year}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Budget (₹)</label>
                                                    <input
                                                        type="number"
                                                        value={budgetForm.budget}
                                                        onChange={(e) => setBudgetForm({ ...budgetForm, budget: e.target.value })}
                                                        placeholder="Enter monthly budget"
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                                                        min="0"
                                                        step="100"
                                                    />
                                                </div>
                                                <button
                                                    type="submit"
                                                    disabled={savingBudget || !budgetForm.budget}
                                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50"
                                                >
                                                    {savingBudget && <Loader2 className="h-4 w-4 animate-spin" />}
                                                    Save Budget
                                                </button>
                                            </form>
                                        </div>

                                        {/* Budget History */}
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-700 mb-3">Budget History</h3>
                                            {budgetsLoading ? (
                                                <div className="text-center py-8 text-gray-500">Loading budgets...</div>
                                            ) : budgets.length === 0 ? (
                                                <div className="text-center py-8">
                                                    <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                                                    <p className="text-sm text-gray-500">No budgets set yet</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    {budgets.map((budget) => (
                                                        <div key={budget.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-2 bg-blue-100 rounded-lg">
                                                                    <Calendar className="h-4 w-4 text-blue-600" />
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">
                                                                        {MONTHS[budget.month - 1]} {budget.year}
                                                                    </p>
                                                                    <p className="text-lg font-semibold text-green-600">
                                                                        ₹{budget.budget.toLocaleString('en-IN')}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <button
                                                                    onClick={() => editBudget(budget)}
                                                                    className="p-1.5 text-gray-400 hover:text-brandColor hover:bg-gray-100 rounded"
                                                                    title="Edit"
                                                                >
                                                                    <Edit2 className="h-4 w-4" />
                                                                </button>
                                                                {deleteBudgetConfirm === budget.id ? (
                                                                    <div className="flex items-center gap-1">
                                                                        <button onClick={() => deleteBudget(budget.id)} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600">Yes</button>
                                                                        <button onClick={() => setDeleteBudgetConfirm(null)} className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded">No</button>
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => setDeleteBudgetConfirm(budget.id)}
                                                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded"
                                                                        title="Delete"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'webhooks' && (
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-medium text-gray-700">Configured Webhooks</h3>

                                        {webhooksLoading ? (
                                            <div className="text-center py-8 text-gray-500">Loading webhooks...</div>
                                        ) : webhooks.length === 0 ? (
                                            <div className="text-center py-8">
                                                <Webhook className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                                                <p className="text-sm text-gray-500">No webhooks configured</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {webhooks.map((webhook) => (
                                                    <div key={webhook.id} className="p-4 bg-white border border-gray-200 rounded-lg">
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-gray-900 truncate">{webhook.webhook_name}</span>
                                                                    <span className={`px-2 py-0.5 text-xs rounded-full ${webhook.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                                        {webhook.is_active ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </div>
                                                                <p className="text-xs text-gray-500 truncate mt-1">{webhook.webhook_url}</p>
                                                            </div>
                                                            <div className="flex items-center gap-1 ml-2">
                                                                <button
                                                                    onClick={() => handleViewLogs(webhook)}
                                                                    className="p-1.5 text-gray-400 hover:text-brandColor hover:bg-gray-100 rounded"
                                                                    title="Activity Logs"
                                                                >
                                                                    <Activity className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleViewStats(webhook)}
                                                                    className="p-1.5 text-gray-400 hover:text-brandColor hover:bg-gray-100 rounded"
                                                                    title="Statistics"
                                                                >
                                                                    <BarChart3 className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Modal */}
            {showContactModal && (
                <div className="fixed inset-0 z-[60] overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black/50" onClick={closeContactModal}></div>
                        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                {editingContact ? 'Edit Contact' : 'Add Contact'}
                            </h3>
                            <form onSubmit={saveContact} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                    <input
                                        type="text"
                                        value={contactForm.name}
                                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                                    <input
                                        type="text"
                                        value={contactForm.designation}
                                        onChange={(e) => setContactForm({ ...contactForm, designation: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                                        placeholder="e.g., Principal, Admin"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <input
                                        type="tel"
                                        value={contactForm.phone}
                                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        value={contactForm.email}
                                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Notification Preferences</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={contactForm.notify_email}
                                                onChange={(e) => setContactForm({ ...contactForm, notify_email: e.target.checked })}
                                                className="mr-2 h-4 w-4 text-brandColor rounded"
                                            />
                                            <Mail className="h-4 w-4 mr-1 text-blue-500" />
                                            <span className="text-sm">Email</span>
                                        </label>
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={contactForm.notify_whatsapp}
                                                onChange={(e) => setContactForm({ ...contactForm, notify_whatsapp: e.target.checked })}
                                                className="mr-2 h-4 w-4 text-brandColor rounded"
                                            />
                                            <MessageCircle className="h-4 w-4 mr-1 text-green-500" />
                                            <span className="text-sm">WhatsApp</span>
                                        </label>
                                    </div>
                                </div>
                                {editingContact && (
                                    <div>
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={contactForm.is_active}
                                                onChange={(e) => setContactForm({ ...contactForm, is_active: e.target.checked })}
                                                className="mr-2 h-4 w-4 text-brandColor rounded"
                                            />
                                            <span className="text-sm">Active</span>
                                        </label>
                                    </div>
                                )}
                                <div className="flex justify-end gap-3 pt-4">
                                    <button type="button" onClick={closeContactModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={savingContact} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50">
                                        {savingContact && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                        {editingContact ? 'Update' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Activity Logs Modal */}
            {showLogsModal && selectedWebhook && (
                <div className="fixed inset-0 z-[70] overflow-hidden">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowLogsModal(false)}></div>
                    <div className="absolute inset-4 md:inset-10 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Activity Logs</h2>
                                <p className="text-sm text-gray-500">Webhook delivery history for {selectedWebhook.webhook_name}</p>
                            </div>
                            <button onClick={() => setShowLogsModal(false)} className="p-2 hover:bg-gray-200 rounded-lg">
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Filters */}
                        <div className="p-4 border-b border-gray-200 bg-gray-50 space-y-3">
                            {/* Status Filter Chips */}
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium text-gray-500">Status:</span>
                                <button
                                    onClick={() => { setLogsStatusFilter(''); handleFilterLogs(''); }}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${logsStatusFilter === '' ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => { setLogsStatusFilter('success'); handleFilterLogs('success'); }}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${logsStatusFilter === 'success' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                                >
                                    <Check className="h-3 w-3" />
                                    Success
                                </button>
                                <button
                                    onClick={() => { setLogsStatusFilter('duplicate'); handleFilterLogs('duplicate'); }}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${logsStatusFilter === 'duplicate' ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
                                >
                                    <AlertCircle className="h-3 w-3" />
                                    Duplicate
                                </button>
                                <button
                                    onClick={() => { setLogsStatusFilter('api_error'); handleFilterLogs('api_error'); }}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${logsStatusFilter === 'api_error' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                >
                                    <X className="h-3 w-3" />
                                    API Error
                                </button>
                                <button
                                    onClick={() => { setLogsStatusFilter('failed'); handleFilterLogs('failed'); }}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${logsStatusFilter === 'failed' ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                                >
                                    <AlertCircle className="h-3 w-3" />
                                    Network Error
                                </button>
                            </div>

                            {/* Search and Date */}
                            <div className="flex items-end gap-3 flex-wrap">
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        <Search className="h-3 w-3 inline mr-1" />
                                        Search
                                    </label>
                                    <input
                                        type="text"
                                        value={logsSearch}
                                        onChange={(e) => setLogsSearch(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleFilterLogs()}
                                        placeholder="Search by phone, name, email..."
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor"
                                    />
                                </div>
                                <div className="min-w-[140px]">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        <Calendar className="h-3 w-3 inline mr-1" />
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={logsStartDate}
                                        onChange={(e) => setLogsStartDate(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor"
                                    />
                                </div>
                                <div className="min-w-[140px]">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        <Calendar className="h-3 w-3 inline mr-1" />
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={logsEndDate}
                                        onChange={(e) => setLogsEndDate(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor"
                                    />
                                </div>
                                <button
                                    onClick={() => handleFilterLogs()}
                                    disabled={logsLoading}
                                    className="px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50 flex items-center gap-2"
                                >
                                    {logsLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                    Search
                                </button>
                                {(logsStartDate || logsEndDate || logsSearch || logsStatusFilter) && (
                                    <button
                                        onClick={() => {
                                            setLogsStartDate('');
                                            setLogsEndDate('');
                                            setLogsSearch('');
                                            setLogsStatusFilter('');
                                            handleViewLogs(selectedWebhook);
                                        }}
                                        className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Logs List */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {logsLoading && logs.length === 0 ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader className="h-8 w-8 animate-spin text-brandColor" />
                                </div>
                            ) : logs.length === 0 ? (
                                <div className="text-center py-12">
                                    <Activity className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                    <p className="text-gray-500">No activity logs yet</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {logs.map((log) => {
                                        const name = log.request_payload?.FirstName || log.request_payload?.name || log.request_payload?.data?.name || '-';
                                        const phone = log.request_payload?.MobileNumber || log.request_payload?.phone || log.request_payload?.data?.phone || '-';
                                        const email = log.request_payload?.Email || log.request_payload?.email || log.request_payload?.data?.email || '-';

                                        const responseResult = log.response_data?.result || log.response_data?.status || log.response_data?.Status;
                                        const responseMessage = log.response_data?.message || log.response_data?.Message || log.response_data?.error || log.response_data?.Error || '';
                                        const isApiSuccess = log.status === 'sent' && (!responseResult || responseResult.toLowerCase() === 'success' || responseResult === true);
                                        const isApiDuplicate = responseMessage?.toLowerCase().includes('already exists') || log.response_data?.leadAlreadyExists === true || log.response_data?.isLeadExists === true;

                                        return (
                                            <div
                                                key={log.id}
                                                className={`border rounded-lg overflow-hidden ${
                                                    log.status === 'failed'
                                                        ? 'border-red-200 bg-red-50/50'
                                                        : isApiSuccess && !isApiDuplicate
                                                            ? 'border-green-200 bg-green-50/50'
                                                            : 'border-amber-200 bg-amber-50/50'
                                                }`}
                                            >
                                                {/* Main Row */}
                                                <div className="p-4">
                                                    <div className="flex items-start justify-between gap-4">
                                                        {/* Lead Info */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="font-medium text-gray-900 truncate">{name}</span>
                                                                {log.status_code && (
                                                                    <span className="text-xs text-gray-500">HTTP {log.status_code}</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                                                <span>{phone}</span>
                                                                {email && email !== '-' && email !== 'null' && (
                                                                    <span className="truncate">{email}</span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                {new Date(log.sent_at).toLocaleString()}
                                                            </div>
                                                        </div>

                                                        {/* Status */}
                                                        <div className="flex-shrink-0 flex items-center gap-2">
                                                            {log.status === 'failed' ? (
                                                                <>
                                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                                        <X className="h-3 w-3" />
                                                                        Failed
                                                                    </span>
                                                                    <button
                                                                        onClick={() => handleResendWebhook(log.id)}
                                                                        disabled={resendingLogId === log.id}
                                                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                                                        title="Resend webhook"
                                                                    >
                                                                        {resendingLogId === log.id ? (
                                                                            <Loader className="h-3 w-3 animate-spin" />
                                                                        ) : (
                                                                            <RotateCcw className="h-3 w-3" />
                                                                        )}
                                                                        Resend
                                                                    </button>
                                                                </>
                                                            ) : isApiSuccess && !isApiDuplicate ? (
                                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                                    <Check className="h-3 w-3" />
                                                                    Success
                                                                </span>
                                                            ) : isApiDuplicate ? (
                                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                                                    <AlertCircle className="h-3 w-3" />
                                                                    Duplicate
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                                        <X className="h-3 w-3" />
                                                                        API Error
                                                                    </span>
                                                                    <button
                                                                        onClick={() => handleResendWebhook(log.id)}
                                                                        disabled={resendingLogId === log.id}
                                                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                                                        title="Resend webhook"
                                                                    >
                                                                        {resendingLogId === log.id ? (
                                                                            <Loader className="h-3 w-3 animate-spin" />
                                                                        ) : (
                                                                            <RotateCcw className="h-3 w-3" />
                                                                        )}
                                                                        Resend
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Response Message */}
                                                    {(log.error_message || responseMessage) && (
                                                        <div className={`mt-3 p-2 rounded text-sm ${
                                                            log.status === 'failed' || (responseResult && responseResult.toLowerCase() === 'failed')
                                                                ? 'bg-red-100 text-red-700'
                                                                : isApiDuplicate
                                                                    ? 'bg-amber-100 text-amber-700'
                                                                    : 'bg-gray-100 text-gray-700'
                                                        }`}>
                                                            <span className="font-medium">Response: </span>
                                                            {log.error_message || responseMessage}
                                                        </div>
                                                    )}

                                                    {/* Expandable Details */}
                                                    <details className="mt-3">
                                                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                                            View full request/response
                                                        </summary>
                                                        <div className="mt-2 grid grid-cols-2 gap-3">
                                                            <div>
                                                                <div className="text-xs font-medium text-gray-700 mb-1">Request Payload</div>
                                                                <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-48 text-gray-600">
                                                                    {JSON.stringify(log.request_payload, null, 2)}
                                                                </pre>
                                                            </div>
                                                            <div>
                                                                <div className="text-xs font-medium text-gray-700 mb-1">Response Data</div>
                                                                <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-48 text-gray-600">
                                                                    {log.response_data ? JSON.stringify(log.response_data, null, 2) : 'No response'}
                                                                </pre>
                                                            </div>
                                                        </div>
                                                    </details>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Load More Section */}
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <div className="text-center text-sm text-gray-500 mb-3">
                                            Showing {logs.length} of {logsTotal} logs
                                        </div>
                                        {logsHasMore && (
                                            <div className="text-center">
                                                <button
                                                    onClick={handleLoadMoreLogs}
                                                    disabled={logsLoading}
                                                    className="px-6 py-2 text-sm font-medium text-brandColor border border-brandColor rounded-lg hover:bg-brandColor hover:text-white disabled:opacity-50 transition-colors inline-flex items-center gap-2"
                                                >
                                                    {logsLoading ? (
                                                        <>
                                                            <Loader className="h-4 w-4 animate-spin" />
                                                            Loading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Plus className="h-4 w-4" />
                                                            Load More
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Stats Modal */}
            {showStatsModal && selectedWebhook && (
                <div className="fixed inset-0 z-[70] overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black/50" onClick={() => setShowStatsModal(false)}></div>
                        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Webhook Statistics</h3>
                                    <p className="text-sm text-gray-500">{selectedWebhook.webhook_name}</p>
                                </div>
                                <button onClick={() => setShowStatsModal(false)} className="p-2 hover:bg-gray-200 rounded-lg">
                                    <X className="h-5 w-5 text-gray-500" />
                                </button>
                            </div>

                            {statsLoading ? (
                                <div className="text-center py-8">
                                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-brandColor" />
                                    <p className="text-gray-500">Loading statistics...</p>
                                </div>
                            ) : stats ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-gray-50 rounded-lg text-center">
                                            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                                            <p className="text-xs text-gray-500">Total Requests</p>
                                        </div>
                                        <div className="p-4 bg-green-50 rounded-lg text-center">
                                            <p className="text-2xl font-bold text-green-600">{stats.success}</p>
                                            <p className="text-xs text-green-600">Success</p>
                                        </div>
                                        <div className="p-4 bg-amber-50 rounded-lg text-center">
                                            <p className="text-2xl font-bold text-amber-600">{stats.duplicate}</p>
                                            <p className="text-xs text-amber-600">Duplicates</p>
                                        </div>
                                        <div className="p-4 bg-red-50 rounded-lg text-center">
                                            <p className="text-2xl font-bold text-red-600">{stats.apiError + stats.networkError}</p>
                                            <p className="text-xs text-red-600">Errors</p>
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-700">Success Rate</span>
                                            <span className="text-sm font-bold text-green-600">{stats.successRate}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div
                                                className="bg-green-500 h-3 rounded-full transition-all"
                                                style={{ width: `${stats.successRate}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">No statistics available</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Automation Settings Modal */}
            {showAutomationModal && selectedSchool && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h2 className="text-base font-semibold text-gray-900">Automation Settings</h2>
                            <button onClick={() => setShowAutomationModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-6 space-y-5">
                            {/* Distance */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Distance Filter</label>
                                <select
                                    value={automationDistance}
                                    onChange={e => setAutomationDistance(Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brandColor"
                                >
                                    <option value={0}>City-based (Boarding School)</option>
                                    {Array.from({ length: 25 }, (_, i) => i + 1).map(km => (
                                        <option key={km} value={km}>{km} km</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    {automationDistance === 0
                                        ? 'Leads from within the same city will be assigned'
                                        : `Only leads within ${automationDistance} km of the school will be assigned`}
                                </p>
                            </div>

                            {/* Grades */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Grade Filter <span className="text-gray-400 font-normal">(leave empty for all grades)</span></label>
                                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                                    {GRADE_OPTIONS.map(opt => (
                                        <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
                                            <input
                                                type="checkbox"
                                                checked={automationGrades.includes(opt.value)}
                                                onChange={e => setAutomationGrades(prev =>
                                                    e.target.checked ? [...prev, opt.value] : prev.filter(g => g !== opt.value)
                                                )}
                                                className="rounded border-gray-300 text-brandColor focus:ring-brandColor"
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                                {automationGrades.length > 0 && (
                                    <button onClick={() => setAutomationGrades([])} className="text-xs text-gray-400 hover:text-gray-600 mt-1">Clear all</button>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-3 px-6 py-4 border-t border-gray-200 justify-end">
                            <button onClick={() => setShowAutomationModal(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                            <button
                                onClick={() => applyAutomation('start', automationDistance, automationGrades)}
                                disabled={updatingAutomation}
                                className="px-4 py-2 text-sm text-white bg-brandColor rounded-lg hover:opacity-90 disabled:opacity-50"
                            >
                                {updatingAutomation ? 'Saving...' : selectedSchool.automation === 'start' ? 'Save Changes' : 'Enable Automation'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientsAdmin;
